// apps/web/src/components/dashboard/OperationalDetailGrid.tsx
//
// KAN-210: graphical command tiles for Operational Detail. The section keeps
// the same T1-T8 concepts and routes, but renders each as an equal-size
// command card with one primary stretched link and one help button.

import type { ReactNode } from 'react'
import { useDashboardTile } from '@/hooks/useDashboardTile'
import { displayMapper } from '@/lib/display-mapper'
import { formatDelta } from '@/lib/delta-format'
import { formatEur } from '@/lib/format-eur'
import type { RegulatoryTileResponse } from '@/hooks/useRegulatoryQueries'
import { TrendDelta } from './TrendDelta'
import {
  AmountCallout,
  DetailCommandCard,
  InfoChipStack,
  MiniSparkline,
  RangeMeter,
  RankStack,
  SegmentedBreakdown,
  T1_CRITICALS_SPARKLINE_CONTRACT,
  T2_RANSOM_SPARKLINE_CONTRACT,
  T6_SUPPLY_CHAIN_SPARKLINE_CONTRACT,
  type OperationalSeries,
  type RankStackRow,
  type SegmentedBreakdownSegment,
} from './operational-detail'

interface T1Response {
  count24h: number
  sevenDayAvg: number
  delta: number
  series?: OperationalSeries<'24h', '4h'>
}
interface T2Response {
  total: number
  topLine: Array<{ countryIso2: string; sector: string; count: number }>
  topGroups?: Array<{ groupName: string; count: number }>
  delta: number
  series?: OperationalSeries<'7d', 'day'>
}
interface T3Response {
  count: number
  breakdown: { kev: number; exploited: number; high: number }
}
interface T4Response {
  count: number
  totalAmountEur: number
  deltaEur: number
  largest: { organisation: string; amountEur: number } | null
}
interface T5Response {
  band: 'red' | 'amber' | 'green'
  ratio: number
  ratioDelta: number
  sector: string | null
}
interface T6Response {
  count: number
  delta: number
  breakdown: { critical: number; high: number; medium: number; low: number }
  series?: OperationalSeries<'7d', 'day'>
}
interface T7Response {
  sufficient: boolean
  buckets: Array<{ countryIso2: string; count: number }>
  fallbackSectors: Array<{ sector: string; count: number }> | null
  // KAN-238: UI-safe reason code (replaces the raw geo-sufficiency math that
  // used to ship in `reason`). Mapped to human copy below — never rendered raw.
  geoReasonCode?: 'insufficient_geo'
}

const BAND_LABEL: Record<T5Response['band'], 'Normal' | 'Elevated' | 'High'> = {
  red: 'High',
  amber: 'Elevated',
  green: 'Normal',
}

function TileIcon({ label }: { label: string }) {
  return <span className="font-mono text-sm font-semibold">{label}</span>
}

function tileStatus(data: unknown, isLoading: boolean, isError: boolean) {
  if (isLoading) return 'loading'
  if (isError) return 'error'
  return data ? 'ready' : 'error'
}

function EmptyGraphic({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[112px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 p-3 text-center text-sm text-muted-foreground">
      {children}
    </div>
  )
}

function EurDelta({ value }: { value: number }) {
  const delta = formatDelta({ value, valuePolarity: 'bad-up' })
  return (
    <span className="font-mono text-xs font-medium text-muted-foreground">
      {delta.arrow} {delta.sign}{formatEur(Math.abs(value))}
    </span>
  )
}

function CriticalsDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T1Response>('criticals-24h')
  return (
    <DetailCommandCard
      id="t1"
      title="Criticals 24h"
      windowLabel="24h"
      href="/stories?severity=critical&since=24h"
      icon={<TileIcon label="T1" />}
      kpi={data ? data.count24h.toLocaleString('en-GB') : '—'}
      contextLine={
        data ? (
          <span>
            <TrendDelta value={data.delta} polarity="bad-up" /> · 7d average{' '}
            {data.sevenDayAvg.toLocaleString('en-GB')}
          </span>
        ) : (
          'Critical story trend unavailable'
        )
      }
      graphic={
        <MiniSparkline
          series={data?.series}
          expected={T1_CRITICALS_SPARKLINE_CONTRACT}
          ariaLabel="Critical stories by 4-hour UTC bucket"
        />
      }
      footer={data?.delta && data.delta > 0 ? 'Above rolling baseline.' : 'Below or equal to rolling baseline.'}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function RansomDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T2Response>('ransom-claims-7d')
  // ransomlook rows are structurally missing victim_country/sector, so the
  // country/sector ranking can be empty while recent claims still exist.
  // Fall back to the top ransomware groups (group_id is always present) so
  // the graphic carries meaningful operational data either way.
  const countrySectorRows: RankStackRow[] = (data?.topLine ?? []).map((item) => ({
    label: `${item.countryIso2} · ${displayMapper.sector(item.sector)}`,
    value: item.count.toLocaleString('en-GB'),
    detail: 'Country and sector bucket',
  }))
  const groupRows: RankStackRow[] = (data?.topGroups ?? []).map((item) => ({
    label: displayMapper.ransomwareGroup(item.groupName),
    value: item.count.toLocaleString('en-GB'),
    detail: 'Ransomware group fallback',
  }))
  const usingCountrySector = countrySectorRows.length > 0
  const rows = usingCountrySector ? countrySectorRows : groupRows

  let footer: string
  if (usingCountrySector) {
    footer = 'Top leak-site claim buckets from the last seven days.'
  } else if (groupRows.length > 0) {
    footer = 'Country/sector coverage sparse — showing top ransomware groups.'
  } else {
    footer = 'No recent leak-site claims to break down.'
  }

  return (
    <DetailCommandCard
      id="t2"
      title="Ransom claims 7d"
      windowLabel="7d"
      href="/threats/leak-sites?since=7d"
      icon={<TileIcon label="T2" />}
      kpi={data ? data.total.toLocaleString('en-GB') : '—'}
      contextLine={data ? <TrendDelta value={data.delta} polarity="bad-up" /> : 'Ransom buckets unavailable'}
      graphic={
        <div className="space-y-3">
          <MiniSparkline
            series={data?.series}
            expected={T2_RANSOM_SPARKLINE_CONTRACT}
            ariaLabel="Leak-site claims by rolling daily UTC bucket"
          />
          {rows.length > 0 ? (
            <RankStack
              rows={rows}
              label={
                usingCountrySector
                  ? 'Top ransomware country and sector buckets'
                  : 'Top ransomware groups'
              }
            />
          ) : (
            <EmptyGraphic>No claim buckets available for this window.</EmptyGraphic>
          )}
        </div>
      }
      footer={footer}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function PatchesDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T3Response>('patches-due')
  const segments: SegmentedBreakdownSegment[] = [
    { label: 'KEV', value: data?.breakdown.kev ?? 0, tone: 'critical' },
    { label: 'Exploited', value: data?.breakdown.exploited ?? 0, tone: 'high' },
    { label: 'High CVSS', value: data?.breakdown.high ?? 0, tone: 'medium' },
  ]

  return (
    <DetailCommandCard
      id="t3"
      title="Patches due"
      windowLabel="7d"
      href="/vulnerabilities?critical=true&since=7d"
      icon={<TileIcon label="T3" />}
      kpi={data ? data.count.toLocaleString('en-GB') : '—'}
      contextLine="critical update set"
      graphic={<SegmentedBreakdown segments={segments} label="Patches due reason breakdown" />}
      footer="KEV, exploited, and CVSS 9+ vulnerabilities updated this week."
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function FinesDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T4Response>('new-fines-30d')
  return (
    <DetailCommandCard
      id="t4"
      title="New fines 30d"
      windowLabel="30d"
      href="/regulatory-actions?since=30d"
      icon={<TileIcon label="T4" />}
      kpi={data ? data.count.toLocaleString('en-GB') : '—'}
      contextLine={data ? <EurDelta value={data.deltaEur} /> : 'Fine trend unavailable'}
      graphic={
        data ? (
          <AmountCallout
            totalAmountEur={data.totalAmountEur}
            deltaEur={data.deltaEur}
            largest={data.largest ?? undefined}
          />
        ) : (
          <EmptyGraphic>No fine detail available.</EmptyGraphic>
        )
      }
      footer={data?.largest ? `Largest enforcement: ${data.largest.organisation}.` : 'No large enforcement action in this window.'}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function SectorHeatDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T5Response>('sector-heat')
  const sectorLabel = data?.sector ? displayMapper.sector(data.sector) : 'all sectors'
  const href = data?.sector
    ? `/analytics?tab=decide&sector=${encodeURIComponent(data.sector)}`
    : '/analytics?tab=decide'

  return (
    <DetailCommandCard
      id="t5"
      title="My sector heat"
      windowLabel="7d"
      href={href}
      icon={<TileIcon label="T5" />}
      kpi={data ? BAND_LABEL[data.band] : '—'}
      contextLine={data ? `${data.ratio.toFixed(2)}× baseline · ${sectorLabel}` : 'Sector heat unavailable'}
      graphic={
        data ? (
          <RangeMeter
            label="Sector heat ratio"
            value={Number(data.ratio.toFixed(2))}
            max={2.5}
            band={data.band}
            bandLabel={BAND_LABEL[data.band]}
          />
        ) : (
          <EmptyGraphic>No sector baseline available.</EmptyGraphic>
        )
      }
      footer={data ? `Band: ${BAND_LABEL[data.band]}. Scope: ${sectorLabel}.` : 'No sector heat detail.'}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function SupplyChainDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T6Response>('supply-chain-7d')
  const segments: SegmentedBreakdownSegment[] = [
    { label: 'Critical', value: data?.breakdown.critical ?? 0, tone: 'critical' },
    { label: 'High', value: data?.breakdown.high ?? 0, tone: 'high' },
    { label: 'Medium', value: data?.breakdown.medium ?? 0, tone: 'medium' },
    { label: 'Low', value: data?.breakdown.low ?? 0, tone: 'low' },
  ]

  return (
    <DetailCommandCard
      id="t6"
      title="Supply-chain 7d"
      windowLabel="7d"
      href="/threats/supply-chain?since=7d"
      icon={<TileIcon label="T6" />}
      kpi={data ? data.count.toLocaleString('en-GB') : '—'}
      contextLine={data ? <TrendDelta value={data.delta} polarity="bad-up" /> : 'Supply-chain trend unavailable'}
      graphic={
        <div className="space-y-3">
          <MiniSparkline
            series={data?.series}
            expected={T6_SUPPLY_CHAIN_SPARKLINE_CONTRACT}
            ariaLabel="Supply-chain incidents by rolling daily UTC bucket"
          />
          <SegmentedBreakdown segments={segments} label="Supply-chain severity breakdown" />
        </div>
      }
      footer="Unsuppressed supply-chain incidents added in the last week."
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function VictimDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T7Response>('victim-countries-30d')
  const showingCountries = Boolean(data?.sufficient && data.buckets.length > 0)
  const buckets: RankStackRow[] = showingCountries
    ? (data?.buckets ?? []).map((bucket) => ({
        label: displayMapper.country(bucket.countryIso2),
        value: bucket.count.toLocaleString('en-GB'),
        detail: 'Country bucket',
      }))
    : (data?.fallbackSectors ?? []).map((bucket) => ({
        label: displayMapper.sector(bucket.sector),
        value: bucket.count.toLocaleString('en-GB'),
        detail: 'Sector fallback',
      }))
  const top = buckets[0]

  // KAN-238: map the UI-safe state to human copy — never render raw geo math.
  // Wording matches the Analytics Decide geo-fallback for cross-surface
  // consistency.
  let footer: string
  if (showingCountries) {
    footer = 'Sufficient sample for victim concentration view.'
  } else if (buckets.length > 0) {
    footer = 'Limited geographic coverage — showing sectors instead.'
  } else {
    footer = 'No recent leak-site claims in the last 30 days.'
  }

  return (
    <DetailCommandCard
      id="t7"
      title={showingCountries ? 'Top victim countries 30d' : 'Top victim sectors 30d'}
      windowLabel="30d"
      href="/analytics?tab=decide&panel=geo"
      icon={<TileIcon label="T7" />}
      kpi={top ? top.label : '—'}
      contextLine={showingCountries ? 'country view' : 'sector fallback'}
      graphic={<RankStack rows={buckets} label="Victim concentration ranked buckets" />}
      footer={footer}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

function RegulatoryDetail({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<RegulatoryTileResponse>(
    'regulatory-tile',
  )

  if (data?.mode === 'cta') {
    return (
      <DetailCommandCard
        id="t8"
        title="Regulatory watch"
        windowLabel="90d"
        href={data.cta.href}
        icon={<TileIcon label="T8" />}
        kpi="Set up"
        contextLine="framework watches"
        graphic={
          <InfoChipStack
            chips={[{ label: 'No watches', detail: 'Setup required', tone: 'neutral' }]}
            ctaCopy={data.cta.label}
            label="Regulatory watch setup labels"
          />
        }
        footer="Watch frameworks to turn this card into a deadline tracker."
        status={tileStatus(data, isLoading, isError)}
        onHelpClick={onHelpClick}
      />
    )
  }

  const isPersonalised = data?.mode === 'personalised'
  const watchedFrameworks = isPersonalised ? data.watchedFrameworks : []
  const frameworks = data
    ? data.mode === 'default'
      ? data.frameworks
      : watchedFrameworks
    : []
  const deadlines = data?.deadlines ?? []
  const href = deadlines.length > 0 ? '/regulatory/calendar' : '/regulatory'

  const deadlineChips = deadlines.slice(0, 2).map((deadline) => ({
    label: deadline.frameworkName,
    detail: deadline.label,
    tone: 'medium' as const,
  }))
  const frameworkChips = frameworks.slice(0, 3).map((framework) => ({
    label: framework.name,
    detail: framework.jurisdiction,
    tone: 'neutral' as const,
  }))
  const chips = [...deadlineChips, ...frameworkChips]

  const kpi = data
    ? (isPersonalised ? watchedFrameworks.length : deadlines.length).toLocaleString('en-GB')
    : '—'
  const contextLine = isPersonalised ? 'watched frameworks' : 'upcoming deadlines'

  // T8 must never render a blank graphic panel. Pick an explicit, honest
  // state for every combination of mode/deadlines/frameworks.
  const nextDeadline = deadlines[0]
  const featuredFramework = frameworks[0]
  let footer: string
  let graphic: ReactNode
  if (nextDeadline) {
    footer = `Next: ${nextDeadline.label} (${nextDeadline.frameworkName}).`
    graphic = (
      <InfoChipStack chips={chips} label="Regulatory framework and deadline labels" />
    )
  } else if (isPersonalised && watchedFrameworks.length > 0) {
    footer = 'No watched-framework deadlines in the next 90 days.'
    graphic = (
      <InfoChipStack chips={frameworkChips} label="Watched regulatory frameworks" />
    )
  } else if (featuredFramework) {
    footer = `Featured: ${featuredFramework.name}.`
    graphic = (
      <InfoChipStack chips={chips} label="Regulatory framework and deadline labels" />
    )
  } else {
    footer = 'No actionable regulatory deadlines in the next 90 days.'
    graphic = (
      <EmptyGraphic>No actionable regulatory deadlines in the next 90 days.</EmptyGraphic>
    )
  }

  return (
    <DetailCommandCard
      id="t8"
      title="Regulatory watch"
      windowLabel="90d"
      href={href}
      icon={<TileIcon label="T8" />}
      kpi={kpi}
      contextLine={contextLine}
      graphic={graphic}
      footer={footer}
      status={tileStatus(data, isLoading, isError)}
      onHelpClick={onHelpClick}
    />
  )
}

interface MetricProps {
  onHelpClick?: (id: string) => void
}

export interface OperationalDetailGridProps {
  onHelpClick?: (tileId: string) => void
}

export function OperationalDetailGrid(props: OperationalDetailGridProps) {
  return (
    <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 2xl:grid-cols-4">
      <CriticalsDetail onHelpClick={props.onHelpClick} />
      <RansomDetail onHelpClick={props.onHelpClick} />
      <PatchesDetail onHelpClick={props.onHelpClick} />
      <FinesDetail onHelpClick={props.onHelpClick} />
      <SectorHeatDetail onHelpClick={props.onHelpClick} />
      <SupplyChainDetail onHelpClick={props.onHelpClick} />
      <VictimDetail onHelpClick={props.onHelpClick} />
      <RegulatoryDetail onHelpClick={props.onHelpClick} />
    </div>
  )
}
